<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "What has happened to my account or my exports that I have not seen yet?"
  Answer:   the feed itself, newest first - an unread row carries a dot plus bold weight,
            never colour alone, since unread is not an ok/danger outcome.
  Context:  each item's timestamp, muted, below its own body text.
  Controls: "Mark as read" per unread row; "Load more" is the one below-the-fold control,
            never styled as the page's primary action.
-->
<script setup lang="ts">
import { ErrorState, PageHeader, PageShell } from '@taler/ui'
import { useI18n } from 'vue-i18n'
import { useSession } from '@/app/session'
import { NotificationList, useNotifications } from '@/features/notifications'

const { t } = useI18n()
const session = useSession()
const notifications = useNotifications()
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('notifications.title')">
      <template #actions>
        <RouterLink
          v-if="session.activeLedger.value !== undefined"
          :to="{ name: 'notification-preferences' }"
          class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] underline"
        >
          {{ t('notificationPreferences.title') }}
        </RouterLink>
      </template>
    </PageHeader>

    <p v-if="notifications.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <ErrorState
      v-else-if="notifications.error.value"
      :title="t('notifications.errorTitle')"
      :body="t('notifications.errorBody')"
      :retry-label="t('notifications.retry')"
      :retrying="notifications.retrying.value"
      @retry="notifications.retry()"
    />

    <NotificationList
      v-else
      :notifications="notifications.notifications.value"
      :active-ledger-id="session.activeLedger.value?.id"
      :has-next-page="notifications.hasNextPage.value"
      :is-fetching-next-page="notifications.isFetchingNextPage.value"
      @mark-read="notifications.markRead($event)"
      @fetch-next-page="notifications.fetchNextPage()"
    />
  </PageShell>
</template>
